import React from "react";
import {
  DeleteOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";

import { Modal } from "../../../../shared";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { useCurationDisplayName } from "../../../../../hooks/useCurationDisplayName";
import CurationEntityName from "../../CurationEntityName";

import "../stylesCollectionsConfiguration.css";

export type DeleteCollectionTarget = {
  code: string;
  name: string;
  name_translations?: Record<string, string>;
  kind: "games" | "providers";
  member_count: number;
};

export type DeleteCollectionConfirmModalProps = {
  open: boolean;
  collection: DeleteCollectionTarget | null;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Fires after antd has finished its close animation. Callers should use
   * this to clear the `collection` target so the modal doesn't unmount
   * mid-animation. Receiving `null` immediately while still `open=true`
   * causes the modal to vanish without playing the slide-out transition,
   * which is jarring next to the rest of the app.
   */
  afterClose?: () => void;
};

/**
 * Destructive-intent confirmation modal for deleting a collection.
 *
 * Renders a summary card (code, name, kind, member count) above a warning
 * paragraph so the admin can verify the exact target before tapping Delete.
 * Wired into the shared `<Modal intent="destructive">` so the primary
 * button picks up the danger styling automatically.
 */
function DeleteCollectionConfirmModal({
  open,
  collection,
  deleting,
  onCancel,
  onConfirm,
  afterClose,
}: DeleteCollectionConfirmModalProps) {
  const { getName } = useCurationDisplayName();
  if (!collection) return null;

  const kindLabel =
    collection.kind === "providers"
      ? AppStrings.CASINO_CONFIG_COLLECTIONS_PROVIDERS_COLLECTION
      : AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_COLLECTION;

  return (
    <Modal
      open={open}
      onClose={deleting ? () => {} : onCancel}
      afterClose={afterClose}
      size="md"
      intent="destructive"
      className="cd-delete-modal"
      title={
        <div className="cd-delete-modal-title-row">
          <ExclamationCircleFilled className="cd-delete-modal-warning-icon" />
          <div>
            <div className="cd-delete-modal-title">
              {AppStrings.CASINO_CONFIG_COLLECTIONS_DELETE_MODAL_TITLE}
            </div>
            <div className="cd-delete-modal-subtitle">
              {AppStrings.CASINO_CONFIG_COLLECTIONS_DELETE_MODAL_SUBTITLE}
            </div>
          </div>
        </div>
      }
      primaryAction={{
        label: (
          <span className="cd-delete-confirm-btn-label">
            <DeleteOutlined />{" "}
            {AppStrings.CASINO_CONFIG_COLLECTIONS_DELETE_BUTTON}
          </span>
        ),
        onClick: onConfirm,
        loading: deleting,
      }}
      secondaryAction={{
        label: AppStrings.CASINO_CONFIG_COLLECTIONS_DELETE_MODAL_CANCEL,
        onClick: onCancel,
        disabled: deleting,
      }}
      data-testid="delete-collection-confirm-modal"
    >
      <div className="cd-delete-modal-card" data-testid="delete-collection-card">
        <div className="cd-delete-modal-card-row">
          <CurationEntityName
            className="cd-delete-modal-card-name"
            entity={collection}
            displayName={getName(collection)}
          />
          <span className="cd-delete-modal-card-code">{collection.code}</span>
        </div>
        <div className="cd-delete-modal-card-meta">
          {kindLabel} &middot; {collection.member_count}{" "}
          {AppStrings.CASINO_CONFIG_COLLECTIONS_DELETE_MEMBERS_SUFFIX}
        </div>
      </div>
      <p className="cd-delete-modal-warning">
        {AppStrings.CASINO_CONFIG_COLLECTIONS_DELETE_MODAL_WARNING}
      </p>
    </Modal>
  );
}

export default DeleteCollectionConfirmModal;
